/*
==========================================================
BerryHome Fullscreen Gallery Controls
Sprint 20.2 — Natural Ratio Fit
Version: 1.9.2

- zachovává funkční logiku Sprintu 20.1A
- zapíná první skutečné zvětšení na desktopu
- zachovává dynamický zoom podle kurzoru
- velikost fullscreen obrázku počítá z naturalWidth / naturalHeight
- obrázek se vejde do viewportu bez deformace
- aktivní médium se přesně vycentruje na výšku i šířku
- bez zásahu do global.js nebo Shopify ModalDialog
==========================================================
*/

(() => {
  const MODAL_SELECTOR = 'product-modal.product-media-modal';
  const MEDIA_SELECTOR =
    '.product-media-modal__content > [data-media-id]';

  const TRANSITION_OUT_MS = 90;
  const TRANSITION_IN_MS = 180;

  const ZOOM_ENABLED = true;
  const ZOOM_SCALE = 1.8;
  const ZOOM_DESKTOP_QUERY =
    '(hover: hover) and (pointer: fine) and (min-width: 750px)';

  function getItems(modal) {
    return Array.from(modal.querySelectorAll(MEDIA_SELECTOR));
  }

  function getActiveIndex(modal) {
    return getItems(modal).findIndex((item) =>
      item.classList.contains('active')
    );
  }

  function getActiveMedia(modal) {
    return modal.querySelector(`${MEDIA_SELECTOR}.active`);
  }

  function getActiveImage(modal) {
  const activeMedia = getActiveMedia(modal);

  if (!activeMedia) return null;

  /* U image media je [data-media-id] přímo samotný <img>. */
  if (
    activeMedia.matches(
      'img:not(.zoomImg):not([role="presentation"])'
    )
  ) {
    return activeMedia;
  }

  /* Video / model / jiné media mohou mít obrázek uvnitř wrapperu. */
  return activeMedia.querySelector(
    'img:not(.zoomImg):not([role="presentation"])'
  );
  }

  function canUseDesktopZoom() {
    return window.matchMedia(ZOOM_DESKTOP_QUERY).matches;
  }

  function setZoomActive(modal, active) {
    if (!ZOOM_ENABLED || !canUseDesktopZoom()) {
      active = false;
    }

    const image = getActiveImage(modal);

    if (!image) {
      active = false;
    }

    modal.classList.toggle('bhfs-zoom-active', active);

    if (active) {
      modal.dataset.bhfsZoomActive = 'true';
      image.style.setProperty(
        '--bhfs-zoom-scale',
        String(ZOOM_SCALE)
      );
    } else {
      delete modal.dataset.bhfsZoomActive;

      if (image) {
        image.style.setProperty('--bhfs-zoom-scale', '1');
      }
    }
  }

  function resetZoomState(modal) {
    const previousImage = modal.querySelector(
      '[data-bhfs-zoom-image="true"]'
    );

    if (previousImage) {
      previousImage.removeAttribute('data-bhfs-zoom-image');
      previousImage.style.removeProperty('--bhfs-zoom-x');
      previousImage.style.removeProperty('--bhfs-zoom-y');
      previousImage.style.removeProperty('--bhfs-zoom-scale');
    }

    modal.classList.remove(
      'bhfs-zoom-ready',
      'bhfs-zoom-active'
    );

    delete modal.dataset.bhfsZoomReady;
    delete modal.dataset.bhfsZoomActive;
  }

  function updateZoomPosition(image, event) {
    const bounds = image.getBoundingClientRect();

    if (!bounds.width || !bounds.height) {
      return;
    }

    const x = Math.min(
      100,
      Math.max(
        0,
        ((event.clientX - bounds.left) / bounds.width) * 100
      )
    );

    const y = Math.min(
      100,
      Math.max(
        0,
        ((event.clientY - bounds.top) / bounds.height) * 100
      )
    );

    image.style.setProperty(
      '--bhfs-zoom-x',
      `${x.toFixed(2)}%`
    );

    image.style.setProperty(
      '--bhfs-zoom-y',
      `${y.toFixed(2)}%`
    );
  }

  function bindZoomEvents(modal, image) {
    if (image.dataset.bhfsZoomBound === 'true') {
      return;
    }

    image.dataset.bhfsZoomBound = 'true';

    image.addEventListener('pointerenter', (event) => {
    if (!modal.hasAttribute('open')) return;

    updateZoomPosition(image, event);

    /* Po otevření fullscreen galerie nezapínat zoom automaticky. */
    setZoomActive(modal, false);
    });

    image.addEventListener('pointermove', (event) => {
      if (!modal.classList.contains('bhfs-zoom-active')) {
        return;
      }

      updateZoomPosition(image, event);
    });

    image.addEventListener('click', (event) => {
    if (!modal.hasAttribute('open')) return;

    event.preventDefault();
    event.stopPropagation();

    const isActive = modal.classList.contains('bhfs-zoom-active');

    if (isActive) {
    setZoomActive(modal, false);

    image.style.setProperty('--bhfs-zoom-x', '50%');
    image.style.setProperty('--bhfs-zoom-y', '50%');
    return;
    }

    updateZoomPosition(image, event);
    setZoomActive(modal, true);
    });

    image.addEventListener('pointerleave', () => {
    /*
    * Zoom ovládáme kliknutím.
    * Opuštění obrázku nesmí aktivní zoom vypnout.
    */
    if (!modal.classList.contains('bhfs-zoom-active')) {
    image.style.setProperty('--bhfs-zoom-x', '50%');
    image.style.setProperty('--bhfs-zoom-y', '50%');
    }
    });
  }

  function prepareZoomInfrastructure(modal) {
    resetZoomState(modal);

    const image = getActiveImage(modal);

    if (!image || !canUseDesktopZoom()) {
      return;
    }

    image.setAttribute('data-bhfs-zoom-image', 'true');
    image.style.setProperty('--bhfs-zoom-x', '50%');
    image.style.setProperty('--bhfs-zoom-y', '50%');
    image.style.setProperty('--bhfs-zoom-scale', '1');

    bindZoomEvents(modal, image);

    modal.dataset.bhfsZoomReady = 'true';
    modal.classList.add('bhfs-zoom-ready');
  }

  function clearFittedImage(modal) {
    const fittedImage = modal.querySelector(
      '[data-bhfs-fitted-image="true"]'
    );

    if (!fittedImage) return;

    fittedImage.removeAttribute('data-bhfs-fitted-image');

    [
      'width',
      'height',
      'max-width',
      'max-height',
      'min-width',
      'min-height',
      'aspect-ratio',
      'object-fit',
      'object-position',
      'margin'
    ].forEach((property) => {
      fittedImage.style.removeProperty(property);
    });

    const activeMedia = fittedImage.closest('[data-media-id]');

    if (activeMedia) {
      [
        'display',
        'align-items',
        'justify-content',
        'position',
        'inset',
        'width',
        'height',
        'min-width',
        'min-height',
        'margin',
        'padding',
        'overflow',
        'z-index'
      ].forEach((property) => {
        activeMedia.style.removeProperty(property);
      });

      activeMedia
        .querySelectorAll('[data-bhfs-fit-wrapper="true"]')
        .forEach((wrapper) => {
          wrapper.removeAttribute('data-bhfs-fit-wrapper');

          [
            'display',
            'align-items',
            'justify-content',
            'width',
            'height',
            'min-width',
            'min-height',
            'margin',
            'padding',
            'overflow'
          ].forEach((property) => {
            wrapper.style.removeProperty(property);
          });
        });
    }
  }

  function fitActiveImage(modal) {
    const image = getActiveImage(modal);

    if (!image) return;

    const applyFit = () => {
      if (!image.naturalWidth || !image.naturalHeight) {
        return;
      }

      clearFittedImage(modal);

      const isMobile = window.innerWidth <= 749;

      const horizontalReserve = isMobile ? 24 : 176;

      /*
      * Fullscreen fit:
      * obrázek musí být celý viditelný ve viewportu.
      * Rezerva chrání horní close button a spodní počítadlo.
      */
      const verticalReserve = isMobile ? 96 : 80;

      const availableWidth = Math.max(
      240,
      document.documentElement.clientWidth - horizontalReserve
      );

      const availableHeight = Math.max(
      240,
      document.documentElement.clientHeight - verticalReserve
      );

      const naturalRatio =
        image.naturalWidth / image.naturalHeight;

      let fittedWidth = availableWidth;
      let fittedHeight = fittedWidth / naturalRatio;

      if (fittedHeight > availableHeight) {
        fittedHeight = availableHeight;
        fittedWidth = fittedHeight * naturalRatio;
      }

      const activeMedia = image.closest('[data-media-id]');

        /*
        * U image media je [data-media-id] přímo <img>,
        * proto kontejnerové styly nesmí být aplikovány na obrázek.
        */
        if (activeMedia && activeMedia !== image) {
        activeMedia.style.setProperty(
          'display',
          'flex',
          'important'
        );
        activeMedia.style.setProperty(
          'align-items',
          'center',
          'important'
        );
        activeMedia.style.setProperty(
          'justify-content',
          'center',
          'important'
        );
        activeMedia.style.setProperty(
          'position',
          'fixed',
          'important'
        );
        activeMedia.style.setProperty(
          'inset',
          '0',
          'important'
        );
        activeMedia.style.setProperty(
          'width',
          '100vw',
          'important'
        );
        activeMedia.style.setProperty(
          'height',
          '100vh',
          'important'
        );
        activeMedia.style.setProperty(
          'min-width',
          '0',
          'important'
        );
        activeMedia.style.setProperty(
          'min-height',
          '0',
          'important'
        );
        activeMedia.style.setProperty(
          'margin',
          '0',
          'important'
        );
        activeMedia.style.setProperty(
          'padding',
          `${Math.round(verticalReserve / 2)}px ${Math.round(horizontalReserve / 2)}px`,
          'important'
        );
        activeMedia.style.setProperty(
          'overflow',
          'hidden',
          'important'
        );
        activeMedia.style.setProperty(
          'z-index',
          '1',
          'important'
        );

        let wrapper = image.parentElement;

        while (wrapper && wrapper !== activeMedia) {
          wrapper.setAttribute(
            'data-bhfs-fit-wrapper',
            'true'
          );

          wrapper.style.setProperty(
            'display',
            'flex',
            'important'
          );
          wrapper.style.setProperty(
            'align-items',
            'center',
            'important'
          );
          wrapper.style.setProperty(
            'justify-content',
            'center',
            'important'
          );
          wrapper.style.setProperty(
            'width',
            '100%',
            'important'
          );
          wrapper.style.setProperty(
            'height',
            '100%',
            'important'
          );
          wrapper.style.setProperty(
            'min-width',
            '0',
            'important'
          );
          wrapper.style.setProperty(
            'min-height',
            '0',
            'important'
          );
          wrapper.style.setProperty(
            'margin',
            '0',
            'important'
          );
          wrapper.style.setProperty(
            'padding',
            '0',
            'important'
          );
          wrapper.style.setProperty(
            'overflow',
            'hidden',
            'important'
          );

          wrapper = wrapper.parentElement;
        }
      }

      image.setAttribute('data-bhfs-fitted-image', 'true');

      image.style.setProperty(
        'width',
        `${Math.round(fittedWidth)}px`,
        'important'
      );
      image.style.setProperty(
        'height',
        `${Math.round(fittedHeight)}px`,
        'important'
      );
      image.style.setProperty(
        'max-width',
        'none',
        'important'
      );
      image.style.setProperty(
        'max-height',
        'none',
        'important'
      );
      image.style.setProperty(
        'min-width',
        '0',
        'important'
      );
      image.style.setProperty(
        'min-height',
        '0',
        'important'
      );
      image.style.setProperty(
        'aspect-ratio',
        'auto',
        'important'
      );
      image.style.setProperty(
        'object-fit',
        'contain',
        'important'
      );
      image.style.setProperty(
        'object-position',
        'center center',
        'important'
      );
      image.style.setProperty(
        'margin',
        'auto',
        'important'
      );

    };

    if (image.complete && image.naturalWidth > 0) {
      applyFit();
    } else {
      image.addEventListener('load', applyFit, { once: true });
    }
  }

  function updateCounter(modal) {
    const items = getItems(modal);
    let activeIndex = getActiveIndex(modal);

    if (activeIndex < 0) activeIndex = 0;

    const current = modal.querySelector(
      '[data-bhfs-modal-current]'
    );
    const total = modal.querySelector(
      '[data-bhfs-modal-total]'
    );
    const previous = modal.querySelector(
      '[data-bhfs-modal-prev]'
    );
    const next = modal.querySelector(
      '[data-bhfs-modal-next]'
    );

    if (current) current.textContent = String(activeIndex + 1);
    if (total) total.textContent = String(items.length);

    const hideNavigation = items.length < 2;

    if (previous) previous.hidden = hideNavigation;
    if (next) next.hidden = hideNavigation;
  }

  function switchActiveMedia(modal, requestedIndex) {
    const items = getItems(modal);

    if (!items.length) return;

    resetZoomState(modal);
    clearFittedImage(modal);

    const index =
      (requestedIndex + items.length) % items.length;

    items.forEach((item, itemIndex) => {
      const active = itemIndex === index;

      item.classList.toggle('active', active);
      item.setAttribute(
        'aria-hidden',
        active ? 'false' : 'true'
      );
    });

    const activeItem = items[index];

    if (
      activeItem.nodeName === 'DEFERRED-MEDIA' &&
      typeof activeItem.loadContent === 'function'
    ) {
      activeItem.loadContent();
    }

    const content = modal.querySelector(
      '.product-media-modal__content'
    );

    if (content) {
      content.scrollTop = 0;
      content.scrollLeft = 0;
    }

    updateCounter(modal);

    window.requestAnimationFrame(() => {
      fitActiveImage(modal);
      prepareZoomInfrastructure(modal);
    });
  }

  function activate(modal, requestedIndex) {
    if (modal.dataset.bhfsSwitching === 'true') {
      return;
    }

    setZoomActive(modal, false);

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (reduceMotion) {
      switchActiveMedia(modal, requestedIndex);
      return;
    }

    modal.dataset.bhfsSwitching = 'true';
    modal.classList.add('bhfs-is-switching');

    window.setTimeout(() => {
      switchActiveMedia(modal, requestedIndex);

      window.requestAnimationFrame(() => {
        modal.classList.remove('bhfs-is-switching');
        modal.classList.add('bhfs-has-switched');

        window.setTimeout(() => {
          modal.classList.remove('bhfs-has-switched');
          delete modal.dataset.bhfsSwitching;
        }, TRANSITION_IN_MS);
      });
    }, TRANSITION_OUT_MS);
  }

  function navigate(modal, direction) {
    const items = getItems(modal);

    if (items.length < 2) return;

    let index = getActiveIndex(modal);

    if (index < 0) index = 0;

    activate(modal, index + direction);
  }

  function stopButtonEvent(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  function bindModal(modal) {
    if (modal.dataset.bhfsControlsBound === 'true') {
      updateCounter(modal);
      fitActiveImage(modal);
      prepareZoomInfrastructure(modal);
      return;
    }

    modal.dataset.bhfsControlsBound = 'true';

    const previous = modal.querySelector(
      '[data-bhfs-modal-prev]'
    );
    const next = modal.querySelector(
      '[data-bhfs-modal-next]'
    );

    [previous, next].forEach((button) => {
      if (!button) return;

      button.addEventListener(
        'pointerdown',
        stopButtonEvent,
        true
      );
    });

    previous?.addEventListener(
      'click',
      (event) => {
        stopButtonEvent(event);
        navigate(modal, -1);
      },
      true
    );

    next?.addEventListener(
      'click',
      (event) => {
        stopButtonEvent(event);
        navigate(modal, 1);
      },
      true
    );

    updateCounter(modal);
    fitActiveImage(modal);
    prepareZoomInfrastructure(modal);

    const observer = new MutationObserver((mutations) => {
      const modalOpened = mutations.some(
        (mutation) =>
          mutation.type === 'attributes' &&
          mutation.attributeName === 'open' &&
          modal.hasAttribute('open')
      );

      window.requestAnimationFrame(() => {
        updateCounter(modal);

        if (modalOpened || modal.hasAttribute('open')) {
          fitActiveImage(modal);
          prepareZoomInfrastructure(modal);
        } else {
          resetZoomState(modal);
          clearFittedImage(modal);
        }
      });
    });

    observer.observe(modal, {
    attributes: true,
    attributeFilter: ['open']
    });
  }

  function modalIsVisible(modal) {
    const style = window.getComputedStyle(modal);

    return (
      modal.hasAttribute('open') ||
      (style.display !== 'none' &&
        style.visibility !== 'hidden')
    );
  }

  function handleKeyboard(event) {
    const modal = Array.from(
      document.querySelectorAll(MODAL_SELECTOR)
    ).find(modalIsVisible);

    if (!modal) return;

    if (
      event.key === 'Escape' &&
      modal.classList.contains('bhfs-zoom-active')
    ) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      setZoomActive(modal, false);
      return;
    }

    if (
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight'
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    navigate(
      modal,
      event.key === 'ArrowLeft' ? -1 : 1
    );
  }

  function handleViewportChange() {
    document
      .querySelectorAll(MODAL_SELECTOR)
      .forEach((modal) => {
        setZoomActive(modal, false);

        if (modal.hasAttribute('open')) {
          fitActiveImage(modal);
          prepareZoomInfrastructure(modal);
        }
      });
  }

  function init(root = document) {
    root
      .querySelectorAll?.(MODAL_SELECTOR)
      .forEach(bindModal);

    console.log(
      'BHFS controls 1.9.2 — Natural ratio fit centered ✓'
    );
  }

  document.addEventListener(
    'keydown',
    handleKeyboard,
    true
  );

  window
    .matchMedia(ZOOM_DESKTOP_QUERY)
    .addEventListener?.('change', handleViewportChange);

  window.addEventListener('resize', handleViewportChange);

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init()
    );
  } else {
    init();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      init(event.target);
    }
  );
})();
